import { StaffDisputeCreditNoteLink, StaffDisputeDetail, StaffDisputeLine } from '../services/staff-dispute.service';

export type DisputeFilter = 'ALL' | 'SUBMITTED' | 'UNDER_REVIEW' | 'APPROVED' | 'REJECTED';

export interface DisputeKpiStats {
    total: number;
    submitted: number;
    underReview: number;
    approved: number;
    rejected: number;
}

export function computeDisputeKpis(disputes: { status?: string }[]): DisputeKpiStats {
    let submitted = 0;
    let underReview = 0;
    let approved = 0;
    let rejected = 0;

    for (const row of disputes) {
        const status = (row.status || '').toUpperCase();
        if (status === 'SUBMITTED') submitted++;
        else if (status === 'UNDER_REVIEW') underReview++;
        else if (status === 'APPROVED' || status === 'PARTIALLY_APPROVED') approved++;
        else if (status === 'REJECTED') rejected++;
    }

    return {
        total: disputes.length,
        submitted,
        underReview,
        approved,
        rejected
    };
}

export function relativeTimeLabel(iso?: string): string {
    if (!iso) return '';
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString();
}

/** Elapsed time between two ISO timestamps (for timeline step gaps). */
export function formatStepDuration(fromIso?: string, toIso?: string): string {
    if (!fromIso || !toIso) return '';
    const fromMs = new Date(fromIso).getTime();
    const toMs = new Date(toIso).getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return '';
    const diffMs = toMs - fromMs;
    if (diffMs < 0) return '';
    const totalMins = Math.floor(diffMs / 60000);
    if (totalMins < 1) return 'Less than 1 min';
    if (totalMins < 60) return `${totalMins} min`;
    const hours = Math.floor(totalMins / 60);
    const mins = totalMins % 60;
    if (hours < 24) {
        return mins > 0 ? `${hours} hr ${mins} min` : `${hours} hr`;
    }
    const days = Math.floor(hours / 24);
    const remHours = hours % 24;
    if (remHours > 0) return `${days} day${days === 1 ? '' : 's'} ${remHours} hr`;
    return `${days} day${days === 1 ? '' : 's'}`;
}

const COGNITO_SUB_PATTERN =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Timeline actor label — email only; never show raw Cognito sub UUIDs. */
export function formatTimelineActor(raw?: string | null): string | null {
    const value = (raw || '').trim();
    if (!value) return null;
    if (value.includes('@')) return value;
    if (COGNITO_SUB_PATTERN.test(value)) return null;
    return value;
}

export function decisionStepLabel(
    status: string,
    approvedDone: boolean,
    rejected: boolean
): string {
    if (rejected) return 'Rejected';
    if (approvedDone) return 'Approved';
    return 'Approved / Rejected';
}

export function statusLabel(status: string): string {
    switch ((status || '').toUpperCase()) {
        case 'UNDER_REVIEW':
            return 'Under Review';
        case 'APPROVED':
        case 'PARTIALLY_APPROVED':
            return 'Approved';
        case 'REJECTED':
            return 'Rejected';
        case 'SUBMITTED':
        default:
            return 'Submitted';
    }
}

export function statusCssClass(status: string): string {
    switch ((status || '').toUpperCase()) {
        case 'UNDER_REVIEW':
            return 'status-review';
        case 'APPROVED':
        case 'PARTIALLY_APPROVED':
            return 'status-approved';
        case 'REJECTED':
            return 'status-rejected';
        default:
            return 'status-submitted';
    }
}

export function matchesDisputeSearch(
    row: { claimNumber?: string; orderNumber?: string; customerName?: string; customerId?: string },
    query: string
): boolean {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [row.claimNumber, row.orderNumber, row.customerName, row.customerId]
        .filter(Boolean)
        .some((v) => (v as string).toLowerCase().includes(q));
}

export function matchesDisputeFilter(status: string | undefined, filter: DisputeFilter): boolean {
    if (filter === 'ALL') return true;
    const s = (status || '').toUpperCase();
    if (filter === 'APPROVED') return s === 'APPROVED' || s === 'PARTIALLY_APPROVED';
    return s === filter;
}

export function formatLocalYmd(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

/** Default list range: today and the previous 7 calendar days (8 days inclusive). */
export function defaultDisputeDateRange(): { fromDate: string; toDate: string } {
    const today = new Date();
    const from = new Date(today);
    from.setDate(today.getDate() - 7);
    return {
        fromDate: formatLocalYmd(from),
        toDate: formatLocalYmd(today)
    };
}

export type CreditNoteOrderTypeId = 'NT3' | 'NT4';

export interface CreditNoteSeedLine {
    orderLineId: number;
    itemNumber: string;
    itemName: string;
    quantity: number;
    rate?: number;
}

export function getCreditNoteTypesForDivision400(): CreditNoteOrderTypeId[] {
    return ['NT3', 'NT4'];
}

export function defaultCreditQtyForLine(line: StaffDisputeLine): number {
    const approved = line.approvedQty ?? line.claimedQty ?? 0;
    const ordered = line.orderedQty ?? 0;
    if (ordered > 0) {
        return Math.min(ordered, approved);
    }
    return approved;
}

export function buildCreditNoteSeedLines(detail: StaffDisputeDetail): CreditNoteSeedLine[] {
    return (detail.lines || []).map((line) => ({
        orderLineId: line.orderLineId,
        itemNumber: line.itemNumber,
        itemName: line.itemName,
        quantity: defaultCreditQtyForLine(line),
        rate: line.unitPriceEur
    }));
}

export function canGenerateCreditNote(detail: StaffDisputeDetail | null, divisionId: string): boolean {
    if (!detail) {
        return false;
    }
    const status = (detail.status || '').toUpperCase();
    if (status !== 'APPROVED' && status !== 'PARTIALLY_APPROVED') {
        return false;
    }
    return String(divisionId).trim() === '400';
}

export function disputeCreditNoteLinks(detail: StaffDisputeDetail | null): StaffDisputeCreditNoteLink[] {
    if (!detail) {
        return [];
    }
    const raw: StaffDisputeCreditNoteLink[] = detail.creditNotes?.length
        ? [...detail.creditNotes]
        : detail.creditNoteOrderNumber
          ? [
                {
                    creditNoteOrderNumber: detail.creditNoteOrderNumber,
                    creditNoteOrderType: detail.creditNoteOrderType || '',
                    createdAt: detail.creditNoteCreatedAt,
                    createdBy: undefined
                }
            ]
          : [];
    const seen = new Set<string>();
    return raw.filter((cn) => {
        const key = (cn.creditNoteOrderNumber || '').trim().toUpperCase();
        if (!key || seen.has(key)) {
            return false;
        }
        seen.add(key);
        return true;
    });
}

export function matchesDisputeDateRange(
    row: { createdAt?: string },
    fromDate: string,
    toDate: string
): boolean {
    if (!fromDate && !toDate) {
        return true;
    }
    if (!row.createdAt) {
        return false;
    }
    const created = new Date(row.createdAt);
    if (Number.isNaN(created.getTime())) {
        return false;
    }
    const ymd = formatLocalYmd(created);
    if (fromDate && ymd < fromDate) {
        return false;
    }
    if (toDate && ymd > toDate) {
        return false;
    }
    return true;
}
